import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {PaymentDetailsResponse} from './types';
import {deletePaymentItem} from '~/api/finance/PaymentDetails';

import {
  PostApplyDiscountVariables,
  getDiscountItems,
  getPaymentDetails,
  postApplyDiscount,
  updatePaymentDescription,
  removeDiscount,
} from '~/api/finance/PaymentDetails';
import {
  IRefundPaymentVariables,
  RefundPaymentResponse,
  postRefundPayment,
} from '~/api/finance/refund';
import toast from '~/utils/toast';
import {ToastType} from '~/components/Toast/constant';
import {handleErrorResponse} from '~/utils/axios';
import {useState} from 'react';
import queryClient from '~/lib/queryClient';
import {getRefundReason} from '~/api/frontdesk';

export function useRefundReason() {
  const {data, isLoading, isError} = useQuery({
    queryKey: ['refund-reason'],
    queryFn: async () => {
      try {
        return await getRefundReason();
      } catch (error) {
        console.error('Failed to fetch refund reasons:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        toast(ToastType.Error, 'Failed to fetch refund reasons', errorMessage);
        throw error;
      }
    },
    retry: 1,
  });

  return {data, isLoading, isError};
}

export function usePaymentDetails(id: string) {
  const query = useQuery<PaymentDetailsResponse>({
    queryKey: ['payment-details', id],
    queryFn: () => getPaymentDetails(id),
    enabled: !!id,
  });
  const data = query.data;

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    payment: data?.data,
    message: data?.message || '',
    success: data?.success || false,
    errors: data?.errors || [],
  };
}

export function useCreateRefundPaymentMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  const {mutate, isPending} = useMutation<
    RefundPaymentResponse,
    Error,
    IRefundPaymentVariables
  >({
    mutationKey: ['create-refund-payment'],
    mutationFn: postRefundPayment,
    onSuccess: (response, variables) => {
      const refundDetails = response?.data?.payment?.refund_details;

      if (refundDetails) {
        queryClient.invalidateQueries({
          queryKey: ['payment-details', variables.payment_id.toString()],
        });
        onSuccess?.();
        toast(ToastType.Success, 'Refund successful', response.message);
      } else {
        toast(ToastType.Error, 'Refund failed', response.message);
      }
    },
    onError: error => {
      handleErrorResponse(error, 'Refund failed');
      const errorMessage = error.message || 'An unexpected error occurred';
      toast(ToastType.Error, 'Refund failed', errorMessage);
    },
  });

  return {
    createRefundPayment: mutate,
    isPending,
  };
}

export function useUpdatePaymentDescription(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  const {mutate, isPending} = useMutation<
    PaymentDetailsResponse,
    Error,
    {id: string; description: string}
  >({
    mutationKey: ['update-payment-description'],
    mutationFn: ({id, description}) =>
      updatePaymentDescription(id, description),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: ['payment-details'],
        });
        onSuccess?.();
        toast(ToastType.Success, 'Success', 'Description updated successfully');
      } else {
        toast(
          ToastType.Error,
          'Failed to update description',
          response.message
        );
      }
    },
    onError: error => {
      handleErrorResponse(error, 'Failed to update description');
      const errorMessage = error.message || 'An unexpected error occurred';
      toast(ToastType.Error, 'Failed to update description', errorMessage);
    },
  });

  return {
    updatePaymentDescription: mutate,
    isPending,
  };
}

export const useDiscountItemsHook = () => {
  const {isPending, isError, error, data} = useQuery({
    queryKey: ['discount-items'],
    queryFn: getDiscountItems,
  });

  return {isPending, isError, error, discountItemsData: data?.data};
};

export function useApplyDiscount() {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate, isPending, isError} = useMutation({
    mutationKey: ['apply-discount'],
    mutationFn: postApplyDiscount,
    onSuccess: data => {
      toast(ToastType.Success, 'Apply discount', data.message);

      queryClient.invalidateQueries({
        queryKey: ['payment-details'],
      });
    },
    onError: error => handleErrorResponse(error, 'Apply discount'),
    onSettled: () => setDisableSubmit(false),
  });

  const sendApplyDiscountRequest = (payload: PostApplyDiscountVariables) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {
    isLoading: disableSubmit || isPending,
    sendApplyDiscountRequest,
    isError,
    isPending,
  };
}

export function useRemoveDiscount() {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate, isPending, isError} = useMutation({
    mutationKey: ['remove-discount'],
    mutationFn: removeDiscount,
    onSuccess: data => {
      toast(ToastType.Success, 'Remove discount', data.message);
      queryClient.invalidateQueries({
        queryKey: ['payment-details'],
      });
    },
    onError: error => handleErrorResponse(error, 'Remove discount'),
    onSettled: () => setDisableSubmit(false),
  });

  const removeDiscountRequest = (paymentId: string) => {
    setDisableSubmit(true);
    mutate(paymentId);
  };

  return {
    isLoading: disableSubmit || isPending,
    removeDiscountRequest,
    isError,
    isPending,
  };
}

export function useDeletePaymentItem() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: deletePaymentItem,
    onSuccess: data => {
      toast(ToastType.Success, 'Delete payment item', data.message);
      queryClient.invalidateQueries({queryKey: ['payment-details']});
    },
    onError: error => handleErrorResponse(error, 'Delete payment item'),
  });

  return {
    deletePaymentItem: mutation.mutate,
    isPending: mutation.isPending,
  };
}
